import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { createCheckout, fetchPlans } from "../services/planService";
import { openPaddleCheckoutForTransaction } from "../services/paddleService";

const LandingPage = () => {
  const REACT_URL = process.env.REACT_APP_URL || "";
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState("");
  const [checkoutLoadingPlanId, setCheckoutLoadingPlanId] = useState(null);

  const platforms = [
    "Twitter/X threads",
    "LinkedIn posts",
    "Meta Threads posts",
  ];
  const steps = [
    {
      step: "01",
      title: "Paste Blog URL",
      desc: "Add any published blog link and BlogSlicer extracts the useful structure, points, and takeaways.",
    },
    {
      step: "02",
      title: "Generate Posts",
      desc: "Create platform-ready drafts for Twitter/X, LinkedIn, and Meta Threads from the same source article.",
    },
    {
      step: "03",
      title: "Edit & Share",
      desc: "Tighten hooks, adjust tone, refine hashtags, and move polished posts into your publishing flow.",
    },
  ];
  const features = [
    {
      title: "Blog URL extraction",
      desc: "Paste a URL and convert long-form blog content into clean source material for social posts.",
    },
    {
      title: "Multi-platform generation",
      desc: "Generate Twitter/X threads, LinkedIn posts, and Meta Threads posts without rewriting the same idea three times.",
    },
    {
      title: "Fast workflow",
      desc: "Move from article link to editable social drafts quickly, so repurposing fits into your publishing rhythm.",
    },
    {
      title: "Editing + customization",
      desc: "Refine hooks, trim sections, rewrite lines, and shape every draft before it goes live.",
    },
    {
      title: "Hashtag + tone improvements",
      desc: "Improve clarity, tone, and discoverability with better phrasing and platform-aware hashtag ideas.",
    },
  ];
  const activePlans = plans.filter((plan) => plan.active === true);

  const formatPrice = (plan) => {
    if (plan.monthly_price === 0) {
      return "$0";
    }

    return `${plan.currency} ${plan.monthly_price} / month`;
  };

  const formatGenerationLimit = (plan) => {
    if (plan.monthly_generation_limit === 0) {
      return "Limited generations";
    }

    return `${plan.monthly_generation_limit} generations / month`;
  };

  const isHighlightedPlan = (plan) => plan.slug === "starter";

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      setPlansLoading(true);
      setPlansError("");

      try {
        const parsedPlans = await fetchPlans();

        if (isMounted) {
          setPlans(parsedPlans);
        }
      } catch (error) {
        if (isMounted) {
          setPlansError(error.message);
        }
      } finally {
        if (isMounted) {
          setPlansLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const goToGoogleAuth = (plan = null) => {
    if (!REACT_URL) {
      console.error("REACT_URL is not configured");
      alert("App misconfigured. Please try again later.");
      return;
    }

    const query = plan ? `?plan=${encodeURIComponent(plan)}` : "";

    window.location.href = `${REACT_URL}/gauth${query}`;
  };

  const handleFreePlanClick = () => {
    goToGoogleAuth();
  };

  const handlePaidPlanCheckout = async (plan) => {
    const token = localStorage.getItem("token");

    if (!token) {
      sessionStorage.setItem(
        "pending_checkout",
        JSON.stringify({
          plan_id: plan.plan_id,
          billing_cycle: "monthly",
        }),
      );
      handleFreePlanClick();
      return;
    }

    setPlansError("");
    setCheckoutLoadingPlanId(plan.plan_id);

    try {
      const checkout = await createCheckout(plan.plan_id, "monthly");

      await openPaddleCheckoutForTransaction(checkout.transactionId);
    } catch (error) {
      if (error.message === "Please log in to continue with checkout.") {
        handleFreePlanClick();
        return;
      }

      setPlansError(error.message || "Unable to start checkout.");
    } finally {
      setCheckoutLoadingPlanId(null);
    }
  };

  return (
    <div className="font-sans text-slate-950 bg-white">
      {/* ================= HEADER ================= */}
      <header className="w-full border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold tracking-tight text-slate-950">
            BlogSlicer
          </div>

          <button
            onClick={() => goToGoogleAuth()}
            className="rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
          >
            Log in
          </button>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-blue-50 via-white to-white px-6 py-20 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto grid gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center"
        >
          <div className="text-center lg:text-left">
            <p className="mb-5 text-sm font-semibold uppercase tracking-wide text-blue-700">
              Blog URL in. Platform-ready posts out.
            </p>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-slate-950 md:text-6xl">
              Turn Blogs Into Social Media Posts
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 md:text-xl">
              BlogSlicer turns one article into editable Twitter/X threads,
              LinkedIn posts, and Meta Threads posts, so every blog can become a
              sharper social campaign in minutes.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start">
              {platforms.map((platform) => (
                <span
                  key={platform}
                  className="rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
                >
                  {platform}
                </span>
              ))}
            </div>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
              <button
                onClick={() => goToGoogleAuth()}
                className="rounded-full bg-blue-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-500"
              >
                Generate Posts Free
              </button>

              <a
                href="#how-it-works"
                className="rounded-full border border-slate-200 bg-white px-8 py-4 text-center text-base font-semibold text-slate-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              >
                See Workflow
              </a>
            </div>

            <p className="mt-4 text-sm text-slate-500">
              No credit card required. Start with a blog URL.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/70">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
              <div className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-500">
                https://yourblog.com/how-to-repurpose-content
              </div>
              <div className="space-y-3">
                {[
                  "Twitter/X thread with a stronger opening hook",
                  "LinkedIn post formatted for professional readers",
                  "Meta Threads post with concise takeaways and hashtags",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 h-2 w-20 rounded-full bg-blue-200" />
                    <p className="text-sm font-medium leading-6 text-slate-700">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between rounded-lg bg-slate-950 px-4 py-3 text-white">
                <span className="text-sm font-semibold">Ready to edit</span>
                <span className="text-xs font-medium text-blue-200">
                  3 platforms
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ================= SOCIAL PROOF ================= */}
      <section className="px-6 py-10 text-center text-sm font-medium text-slate-500">
        Built for bloggers, founders, and creators who publish once and share
        everywhere.
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how-it-works" className="px-6 py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 mb-3">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-950">
              From blog URL to shareable posts in three steps
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              A focused workflow for turning long-form ideas into social content
              you can review, edit, and publish.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-xl border border-slate-200 bg-white p-7 shadow-sm"
              >
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white shadow-lg shadow-blue-100">
                  {item.step}
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-950">
                  {item.title}
                </h3>
                <p className="leading-7 text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 mb-3">
              Features
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-950 mb-4">
              Everything you need for blog-to-social repurposing
            </h2>
            <p className="text-lg leading-8 text-slate-600">
              Keep the workflow simple: capture the article, generate posts for
              each platform, then edit the copy until it sounds like you.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              >
                <div className="mb-5 h-10 w-10 rounded-lg bg-blue-50" />
                <h3 className="text-xl font-semibold text-slate-950 mb-3">
                  {f.title}
                </h3>
                <p className="text-slate-600 leading-7">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="bg-slate-50 py-24 px-6">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-blue-700">
            Pricing
          </p>
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
            Simple pricing for a simple workflow
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">
            Start free, then upgrade when blog repurposing becomes part of your
            content engine.
          </p>

          {plansError && (
            <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {plansError}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plansLoading && (
            <p className="rounded-xl border border-slate-200 bg-white p-8 text-center font-medium text-slate-600 md:col-span-3">
              Loading plans...
            </p>
          )}

          {!plansLoading &&
            activePlans.map((plan, i) => (
              <motion.div
                key={plan.plan_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative rounded-2xl border bg-white p-8 shadow-sm ${
                  isHighlightedPlan(plan)
                    ? "border-blue-600 shadow-xl shadow-blue-100 md:-mt-4"
                    : "border-slate-200"
                }`}
              >
                {isHighlightedPlan(plan) && (
                  <div className="absolute right-6 top-6 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    Popular
                  </div>
                )}

                <h3 className="text-xl font-bold text-slate-950 mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm font-medium text-slate-500 mb-6">
                  {plan.description}
                </p>
                <p className="text-4xl font-bold tracking-tight text-slate-950 mb-8">
                  {formatPrice(plan)}
                </p>

                <ul className="mb-8 space-y-3 text-left text-slate-600">
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">
                      ✓
                    </span>
                    <span>{formatGenerationLimit(plan)}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">
                      ✓
                    </span>
                    <span>Twitter/X, LinkedIn, and Meta Threads drafts</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">
                      ✓
                    </span>
                    <span>Editable generated posts</span>
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() =>
                    plan.monthly_price === 0
                      ? handleFreePlanClick()
                      : handlePaidPlanCheckout(plan)
                  }
                  disabled={checkoutLoadingPlanId === plan.plan_id}
                  className={`w-full rounded-full px-6 py-3 font-semibold transition ${
                    isHighlightedPlan(plan)
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-100 hover:bg-blue-500"
                      : "border border-slate-200 bg-white text-slate-800 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {checkoutLoadingPlanId === plan.plan_id
                    ? "Redirecting..."
                    : plan.monthly_price === 0
                      ? "Get Started"
                      : `Choose ${plan.name}`}
                </button>
              </motion.div>
            ))}
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="bg-slate-950 px-6 py-8 text-center text-sm text-slate-300">
        <p className="mb-3">
          © {new Date().getFullYear()} BlogSlicer. All rights reserved.
        </p>
        <div className="space-x-4">
          <a href="/termsandconditions" className="underline hover:text-white">
            Terms
          </a>
          <a href="/termsandconditions" className="underline hover:text-white">
            Privacy
          </a>
          <a href="/termsandconditions" className="underline hover:text-white">
            Refunds
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
